import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { css } from "styled-system/css";
import Markdown from "react-markdown";
import { Card, CardHeader, CardTitle, CardContent } from "~/components/ui/Card";
import { api } from "~/lib/api";
import { Sparkles, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";

interface AIInsights {
  summary: string;
  strengths: string[];
  growthAreas: string[];
  highlights: string[];
  workStyle: string;
  generatedAt: string;
  cached: boolean;
}

interface AIReviewSectionProps {
  developerId: string;
  developerName: string;
  range?: string;
}

export function AIReviewSection({
  developerId,
  developerName,
  range = "90d",
}: AIReviewSectionProps) {
  const [streamedText, setStreamedText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [cachedInsights, setCachedInsights] = useState<AIInsights | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Try to fetch cached insights first (non-streaming)
  const { data: initialData, isLoading: isLoadingInitial } = useQuery({
    queryKey: ["developer-insights", developerId, range],
    queryFn: () =>
      api.get<{ data: { insights: AIInsights } }>(
        `/developers/${developerId}/insights?range=${range}`,
      ),
    enabled: !!developerId,
    staleTime: 7 * 24 * 60 * 60 * 1000, // 7 days - match server cache
    retry: false,
  });

  // Show cached insights when they arrive
  const [seenInitial, setSeenInitial] = useState<typeof initialData>();
  if (initialData !== seenInitial) {
    setSeenInitial(initialData);
    if (initialData?.data?.insights) {
      setCachedInsights(initialData.data.insights);
    }
  }

  const startStreaming = useCallback(
    async (forceRefresh = false) => {
      setIsStreaming(true);
      setStreamedText("");
      setError(null);
      if (forceRefresh) {
        setCachedInsights(null); // Clear cached display while regenerating
      }

      try {
        const refreshParam = forceRefresh ? "&refresh=true" : "";
        const response = await fetch(
          `/api/developers/${developerId}/insights/stream?range=${range}${refreshParam}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch insights");
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("No reader available");
        }

        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.slice(6);

              if (data === "[DONE]") {
                setIsStreaming(false);
                return;
              }

              try {
                const parsed = JSON.parse(data);

                if (parsed.type === "cached") {
                  // Got cached response - use it directly
                  setCachedInsights(parsed.data);
                  setIsStreaming(false);
                  return;
                } else if (parsed.type === "text") {
                  setStreamedText((prev) => prev + parsed.content);
                } else if (parsed.type === "error") {
                  setError(parsed.message);
                  setIsStreaming(false);
                  return;
                }
              } catch {
                // Skip malformed JSON
              }
            }
          }
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to stream insights",
        );
      } finally {
        setIsStreaming(false);
      }
    },
    [developerId, range],
  );

  // Parse streamed text into sections
  const parseStreamedText = (text: string) => {
    const sections: {
      summary?: string;
      strengths: string[];
      growthAreas: string[];
      highlights: string[];
      workStyle?: string;
    } = {
      strengths: [],
      growthAreas: [],
      highlights: [],
    };

    // Extract SUMMARY
    const summaryMatch = text.match(
      /\*\*SUMMARY\*\*[^\n]*\n([\s\S]*?)(?=\*\*STRENGTHS\*\*|$)/i,
    );
    if (summaryMatch?.[1]) {
      sections.summary = summaryMatch[1].trim();
    }

    // Extract STRENGTHS
    const strengthsMatch = text.match(
      /\*\*STRENGTHS\*\*[^\n]*\n([\s\S]*?)(?=\*\*GROWTH_AREAS\*\*|$)/i,
    );
    if (strengthsMatch?.[1]) {
      sections.strengths = extractBulletPoints(strengthsMatch[1]);
    }

    // Extract GROWTH_AREAS
    const growthMatch = text.match(
      /\*\*GROWTH_AREAS\*\*[^\n]*\n([\s\S]*?)(?=\*\*HIGHLIGHTS\*\*|$)/i,
    );
    if (growthMatch?.[1]) {
      sections.growthAreas = extractBulletPoints(growthMatch[1]);
    }

    // Extract HIGHLIGHTS
    const highlightsMatch = text.match(
      /\*\*HIGHLIGHTS\*\*[^\n]*\n([\s\S]*?)(?=\*\*WORK_STYLE\*\*|$)/i,
    );
    if (highlightsMatch?.[1]) {
      sections.highlights = extractBulletPoints(highlightsMatch[1]);
    }

    // Extract WORK_STYLE
    const workStyleMatch = text.match(/\*\*WORK_STYLE\*\*[^\n]*\n([\s\S]*?)$/i);
    if (workStyleMatch?.[1]) {
      sections.workStyle = workStyleMatch[1].trim().replace(/^["']|["']$/g, "");
    }

    return sections;
  };

  const extractBulletPoints = (text: string): string[] => {
    const lines = text.split("\n");
    const bullets: string[] = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.match(/^[-*]\s+/) || trimmed.match(/^\d+\.\s+/)) {
        const content = trimmed
          .replace(/^[-*]\s+/, "")
          .replace(/^\d+\.\s+/, "")
          .trim();
        if (content) {
          bullets.push(content);
        }
      }
    }

    return bullets;
  };

  // Determine what to render
  const hasContent = cachedInsights || streamedText;
  const parsedStreamed = streamedText ? parseStreamedText(streamedText) : null;

  // Use cached insights if available, otherwise use parsed streamed text
  const displayData =
    cachedInsights ||
    (parsedStreamed?.summary
      ? {
          summary: parsedStreamed.summary,
          strengths: parsedStreamed.strengths,
          growthAreas: parsedStreamed.growthAreas,
          highlights: parsedStreamed.highlights,
          workStyle: parsedStreamed.workStyle || "",
        }
      : null);

  return (
    <Card className={styles.card}>
      <CardHeader className={styles.header}>
        <div className={styles.titleRow}>
          <div className={styles.titleWithIcon}>
            <Sparkles size={18} className={styles.sparklesIcon} />
            <CardTitle>AI Review</CardTitle>
          </div>
          <div className={styles.actions}>
            {!isStreaming && (
              <button
                onClick={() => startStreaming(true)}
                className={styles.refreshButton}
                title="Regenerate insights"
              >
                <RefreshCw size={14} />
              </button>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={styles.expandButton}
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>
        {displayData?.workStyle && (
          <div className={styles.workStyleBadge}>{displayData.workStyle}</div>
        )}
      </CardHeader>

      {isExpanded && (
        <CardContent>
          {isLoadingInitial && !hasContent && (
            <div className={styles.loading}>
              <div className={styles.skeleton} />
              <div className={styles.skeleton} style={{ width: "80%" }} />
              <div className={styles.skeleton} style={{ width: "60%" }} />
            </div>
          )}

          {isStreaming && !displayData && (
            <div className={styles.streamingContainer}>
              <div className={styles.streamingIndicator}>
                <Sparkles size={14} className={styles.pulsingIcon} />
                <span>Generating insights for {developerName}...</span>
              </div>
              {streamedText && (
                <div className={styles.streamingText}>
                  {streamedText}
                  <span className={styles.cursor}>|</span>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className={styles.error}>
              {error}
              <button
                onClick={() => startStreaming(true)}
                className={styles.retryButton}
              >
                Try Again
              </button>
            </div>
          )}

          {displayData && !isStreaming && (
            <div className={styles.content}>
              {displayData.summary && (
                <div className={styles.section}>
                  <div className={styles.summary}>
                    <Markdown>{displayData.summary}</Markdown>
                  </div>
                </div>
              )}

              <div className={styles.grid}>
                {displayData.strengths && displayData.strengths.length > 0 && (
                  <div className={styles.listSection}>
                    <h4 className={styles.listTitle}>Strengths</h4>
                    <ul className={styles.list}>
                      {displayData.strengths.map((item, idx) => (
                        <li
                          key={idx}
                          className={styles.listItem}
                          data-type="strength"
                        >
                          <Markdown>{item}</Markdown>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {displayData.growthAreas &&
                  displayData.growthAreas.length > 0 && (
                    <div className={styles.listSection}>
                      <h4 className={styles.listTitle}>Growth Areas</h4>
                      <ul className={styles.list}>
                        {displayData.growthAreas.map((item, idx) => (
                          <li
                            key={idx}
                            className={styles.listItem}
                            data-type="growth"
                          >
                            <Markdown>{item}</Markdown>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>

              {displayData.highlights && displayData.highlights.length > 0 && (
                <div className={styles.highlightsSection}>
                  <h4 className={styles.listTitle}>Recent Highlights</h4>
                  <ul className={styles.highlightsList}>
                    {displayData.highlights.map((item, idx) => (
                      <li key={idx} className={styles.highlightItem}>
                        <Markdown>{item}</Markdown>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {!hasContent && !isLoadingInitial && !isStreaming && !error && (
            <div className={styles.empty}>
              <button
                onClick={() => startStreaming()}
                className={styles.generateButton}
              >
                <Sparkles size={16} />
                Generate AI Review
              </button>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

const styles = {
  // Editorial page: no card chrome, just the section's rules
  card: css({
    mb: 4,
    border: "none",
    borderRadius: "0",
    bg: "transparent",
    fontFamily: "serif",
    "& > div": { px: 0 },
    "& h3": { fontFamily: "sans", fontWeight: 700 },
  }),
  header: css({
    pb: 2,
  }),
  titleRow: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  }),
  titleWithIcon: css({
    display: "flex",
    alignItems: "center",
    gap: 2,
  }),
  sparklesIcon: css({
    color: "ed.ink",
  }),
  actions: css({
    display: "flex",
    alignItems: "center",
    gap: 1,
  }),
  refreshButton: css({
    p: 1.5,
    borderRadius: "0",
    color: "ed.ink3",
    cursor: "pointer",
    transition: "all 0.15s ease",
    bg: "transparent",
    border: "none",
    _hover: {
      bg: "ed.panel",
      color: "ed.ink",
    },
  }),
  expandButton: css({
    p: 1.5,
    borderRadius: "0",
    color: "ed.ink3",
    cursor: "pointer",
    transition: "all 0.15s ease",
    bg: "transparent",
    border: "none",
    _hover: {
      bg: "ed.panel",
      color: "ed.ink",
    },
  }),
  workStyleBadge: css({
    display: "inline-block",
    mt: 2,
    px: 2,
    py: 0.5,
    fontSize: "xs",
    fontWeight: "medium",
    color: "ed.ink",
    bg: "ed.panel",
    borderRadius: "full",
  }),
  loading: css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
  }),
  skeleton: css({
    height: 4,
    bg: "ed.panel",
    borderRadius: "0",
    animation: "pulse 2s infinite",
  }),
  streamingContainer: css({
    display: "flex",
    flexDirection: "column",
    gap: 3,
  }),
  streamingIndicator: css({
    display: "flex",
    alignItems: "center",
    gap: 2,
    fontSize: "sm",
    color: "ed.ink2",
  }),
  pulsingIcon: css({
    animation: "pulse 1.5s infinite",
  }),
  streamingText: css({
    fontSize: "sm",
    color: "ed.ink3",
    whiteSpace: "pre-wrap",
    fontFamily: "mono",
    bg: "ed.panel",
    p: 3,
    borderRadius: "0",
    maxHeight: "300px",
    overflow: "auto",
  }),
  cursor: css({
    animation: "blink 1s infinite",
  }),
  error: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    p: 3,
    bg: "ed.cautionTint",
    color: "ed.caution",
    borderRadius: "0",
    fontSize: "sm",
  }),
  retryButton: css({
    px: 3,
    py: 1,
    fontSize: "sm",
    fontWeight: "medium",
    color: "ed.caution",
    bg: "ed.paper",
    border: "1px solid",
    borderColor: "ed.caution",
    borderRadius: "0",
    cursor: "pointer",
    _hover: {
      bg: "ed.cautionTint",
    },
  }),
  content: css({
    display: "flex",
    flexDirection: "column",
    gap: 4,
  }),
  section: css({}),
  summary: css({
    fontSize: "sm",
    color: "ed.ink",
    lineHeight: 1.6,
    "& p": {
      m: 0,
    },
    "& strong": {
      fontWeight: "semibold",
    },
    "& code": {
      fontSize: "xs",
      bg: "ed.panel",
      px: 1,
      py: 0.5,
      borderRadius: "0",
    },
  }),
  grid: css({
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 4,
  }),
  listSection: css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
  }),
  listTitle: css({
    fontSize: "xs",
    fontWeight: "semibold",
    color: "ed.ink3",
    textTransform: "uppercase",
    letterSpacing: "wide",
  }),
  list: css({
    listStyle: "none",
    m: 0,
    p: 0,
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
  }),
  listItem: css({
    fontSize: "sm",
    color: "ed.ink",
    pl: 4,
    position: "relative",
    _before: {
      content: '""',
      position: "absolute",
      left: 0,
      top: "0.5em",
      width: 2,
      height: 2,
      borderRadius: "full",
    },
    '&[data-type="strength"]::before': {
      bg: "ed.ink",
    },
    '&[data-type="growth"]::before': {
      bg: "ed.ink3",
    },
    "& p": {
      m: 0,
    },
    "& strong": {
      fontWeight: "semibold",
    },
    "& code": {
      fontSize: "xs",
      bg: "ed.panel",
      px: 1,
      py: 0.5,
      borderRadius: "0",
    },
  }),
  highlightsSection: css({
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderColor: "ed.rule",
    pt: 4,
  }),
  highlightsList: css({
    listStyle: "none",
    m: 0,
    p: 0,
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
  }),
  highlightItem: css({
    fontSize: "xs",
    color: "ed.ink",
    bg: "ed.panel",
    px: 2.5,
    py: 1,
    borderRadius: "full",
    "& p": {
      m: 0,
    },
    "& strong": {
      fontWeight: "semibold",
    },
  }),
  empty: css({
    display: "flex",
    justifyContent: "center",
    py: 6,
  }),
  generateButton: css({
    display: "flex",
    alignItems: "center",
    gap: 2,
    px: 4,
    py: 2,
    fontSize: "sm",
    fontWeight: "medium",
    color: "ed.paper",
    bg: "ed.ink",
    border: "none",
    borderRadius: "0",
    cursor: "pointer",
    transition: "all 0.15s ease",
    _hover: {
      bg: "ed.ink2",
    },
  }),
};
